import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, MessageCircle, CalendarCheck, ArrowLeft, ShieldCheck } from "lucide-react";
import { AREAS, SITE, PRICING, waLink } from "@/lib/site";
import { WhatsAppFloat } from "@/components/gogo/WhatsAppFloat";

const POPULAR = [
  PRICING[0].rows[1],
  PRICING[0].rows[3],
  PRICING[1].rows[0],
  PRICING[2].rows[0],
];

export default function AreaPage() {
  const { slug } = useParams();
  const area = AREAS[slug];

  useEffect(() => {
    document.title = area
      ? `Mobile Mechanic in ${area.name} | GoGoAuto Mobile Repair & Service`
      : "Area Not Found | GoGoAuto Mobile Repair & Service";
    return () => {
      document.title = "Mobile Mechanic Scarborough & GTA — GoGoAuto Mobile Repair";
    };
  }, [area]);

  if (!area) {
    return (
      <div className="min-h-screen bg-obsidian flex flex-col items-center justify-center px-4 text-center">
        <p className="font-display font-extrabold uppercase text-4xl">Area not found</p>
        <a href="/" className="mt-6 text-ember font-display font-bold uppercase tracking-wider hover:underline" data-testid="area-notfound-home">
          ← Back to homepage
        </a>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-obsidian noise-overlay relative" data-testid="area-page">
      <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-md bg-[#22262B]/85 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-[72px] flex items-center justify-between">
          <a href="/" data-testid="area-header-logo" className="flex items-center">
            <img src="/logo.jpg" alt="GoGoAuto Mobile Repair & Service" className="h-11 w-auto rounded-sm" />
          </a>
          <a
            href="/#booking"
            data-testid="area-header-book"
            className="bg-ember text-obsidian font-display font-bold uppercase tracking-wider text-base px-5 py-2 rounded-sm hover:bg-[#9BE873] transition-colors"
          >
            Book Service
          </a>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}>
          <a href="/" className="inline-flex items-center gap-2 text-mist text-sm hover:text-ember transition-colors mb-8" data-testid="area-back-home">
            <ArrowLeft className="w-4 h-4" /> All service areas
          </a>
          <p className="font-mono2 text-xs tracking-[0.35em] uppercase text-ember mb-4 flex items-center gap-2">
            <MapPin className="w-4 h-4" /> Mobile Mechanic — {area.name}
          </p>
          <h1 className="font-display font-black uppercase leading-[0.95] tracking-tight text-5xl sm:text-6xl lg:text-7xl" data-testid="area-headline">
            Mobile mechanic<br />in <span className="text-ember">{area.name}</span>
          </h1>
          <p className="mt-6 text-base text-[#C9D2DC] leading-relaxed max-w-2xl">{area.copy1}</p>
          <p className="mt-4 text-base text-[#C9D2DC] leading-relaxed max-w-2xl">{area.copy2}</p>
          <p className="mt-4 text-base text-[#C9D2DC] leading-relaxed max-w-2xl">{area.copy3}</p>
          <p className="mt-6 font-mono2 text-[11px] tracking-[0.2em] uppercase text-mist" data-testid="area-hoods">{area.hoods}</p>

          <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-mist">
            <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-ember" /> Licensed 310S Master Technician</span>
            <span className="flex items-center gap-2"><MapPin className="w-4 h-4 text-ember" /> $100 flat call-out, often included</span>
          </div>

          <div className="mt-10 flex flex-wrap gap-4">
            <a
              href="/#booking"
              data-testid="area-book-btn"
              className="inline-flex items-center gap-2 bg-ember text-obsidian font-display font-bold uppercase tracking-wider text-lg px-8 py-4 rounded-sm hover:bg-[#9BE873] hover:shadow-[0_0_32px_rgba(132,220,90,0.35)] transition-all duration-200"
            >
              <CalendarCheck className="w-5 h-5" /> Book Service Online
            </a>
            <a
              href={waLink(`Hi GoGoAuto! I'm in ${area.name} and need a mobile mechanic.`)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="area-whatsapp-btn"
              className="inline-flex items-center gap-2 border border-[#25D366]/60 text-[#25D366] font-display font-bold uppercase tracking-wider text-lg px-8 py-4 rounded-sm hover:bg-[#25D366] hover:text-obsidian transition-all duration-200"
            >
              <MessageCircle className="w-5 h-5" /> WhatsApp {SITE.phoneDisplay}
            </a>
          </div>

          <div className="mt-12 bg-charcoal border border-white/10 rounded-sm overflow-hidden" data-testid="area-popular-services">
            <p className="px-7 py-5 border-b border-white/10 font-display font-bold uppercase text-2xl tracking-tight">
              Popular in {area.name}
            </p>
            <ul>
              {POPULAR.map((row) => (
                <li key={row.name} className="px-7 py-4 flex items-start justify-between gap-6 border-b border-white/5 last:border-0">
                  <div>
                    <p className="font-semibold text-sm text-white">{row.name}</p>
                    <p className="text-xs text-mist mt-0.5">{row.detail}</p>
                  </div>
                  <span className="font-mono2 text-sm font-bold text-ember whitespace-nowrap pt-0.5">{row.price}</span>
                </li>
              ))}
            </ul>
            <a href="/#pricing" className="block px-7 py-4 text-ember font-display font-bold uppercase tracking-wider text-sm hover:bg-white/5 transition-colors" data-testid="area-pricing-link">
              See the full pricing schedule →
            </a>
          </div>
        </motion.div>
      </main>
      <WhatsAppFloat />
    </div>
  );
}
